import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import HeaderSection from '../../components/home/HeaderSection';
import FooterSection from '../../components/home/FooterSection';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import DoubtsDiscussionSection from '../../components/playlist/DoubtsDiscussionSection';
import OfflineDownloadsPanel from '../../components/playlist/OfflineDownloadsPanel';
import PlaylistChapterPicker from '../../components/playlist/PlaylistChapterPicker';
import PlaylistLectureInfo from '../../components/playlist/PlaylistLectureInfo';
import PlaylistPageHeader from '../../components/playlist/PlaylistPageHeader';
import PlaylistPlayerSection from '../../components/playlist/PlaylistPlayerSection';
import { useAuthContext } from '../../context/AuthContext';
import { useCanonicalCourseUrl } from '../../hooks/useCanonicalCourseUrl';
import { usePlaylistPage } from '../../hooks/usePlaylistPage';
import { useVdoCipherOffline } from '../../hooks/useVdoCipherOffline';
import { publishAppToast } from '../../utils/appToast';
import { parseCourseIdFromSlug } from '../../utils/courseSlug';

function PlaylistPageSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="h-10 w-2/3 rounded bg-gray-200" />
      <div className="aspect-video rounded-2xl bg-gray-200" />
      <div className="h-24 rounded-2xl bg-gray-100" />
    </div>
  );
}

function VideoPlayerFallback({ message, onRetry }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-black shadow-lg">
      <div className="flex aspect-video w-full flex-col items-center justify-center px-6 text-center text-white">
        <p className="text-base font-semibold sm:text-lg">Unable to load video</p>
        <p className="mt-2 max-w-md text-sm text-white/75">
          {message || 'Something went wrong while loading the lesson player.'}
        </p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-5 rounded-full bg-[#9369CC] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#7f55b8]"
          >
            Try again
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function PlaylistPage() {
  const { courseId: courseSlug } = useParams();
  const courseId = parseCourseIdFromSlug(courseSlug);
  const { isLoggedIn } = useAuthContext();
  const [playerErrorMessage, setPlayerErrorMessage] = useState(null);
  const [playerResetKey, setPlayerResetKey] = useState(0);
  const lastOfflineErrorRef = useRef(null);

  const handleVideoError = useCallback((message) => {
    setPlayerErrorMessage(message);
    publishAppToast(message, 'error');
  }, []);

  const {
    chapterId,
    lessonId,
    chapterTitle,
    breadcrumbs,
    playlist,
    courseTitle,
    activeLesson,
    chapters,
    loading,
    error,
    selectChapter,
    selectLesson,
    markLessonProgress,
    markLessonComplete,
    refetch: refetchPlaylist,
  } = usePlaylistPage(courseId);

  useCanonicalCourseUrl(
    courseId && courseTitle ? { id: courseId, title: courseTitle } : null,
    { playlist: true }
  );

  const {
    offlineVideos,
    loading: offlineLoading,
    actionLoading: offlineActionLoading,
    error: offlineError,
    removeLessons,
    removeCourseDownloads,
    refetch: refetchOfflineDownloads,
  } = useVdoCipherOffline({
    courseId,
    enabled: isLoggedIn && Boolean(courseId),
  });

  const handleLessonProgress = useCallback(
    (update) => {
      if (!lessonId) return;
      markLessonProgress(lessonId, update);
    },
    [lessonId, markLessonProgress]
  );

  const handleLessonComplete = useCallback(() => {
    if (lessonId) {
      markLessonComplete(lessonId);
    }
  }, [lessonId, markLessonComplete]);

  const handleRemoveOfflineLesson = async (targetLessonId) => {
    const success = await removeLessons({
      lesson_ids: [targetLessonId],
      lesson_id: targetLessonId,
    });
    if (success) {
      void refetchOfflineDownloads();
      void refetchPlaylist();
    }
  };

  const handleRemoveAllOffline = async () => {
    const success = await removeCourseDownloads(courseId);
    if (success) {
      void refetchOfflineDownloads();
      void refetchPlaylist();
    }
  };

  const retryPlayer = useCallback(() => {
    setPlayerErrorMessage(null);
    setPlayerResetKey((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!offlineError || offlineError === lastOfflineErrorRef.current) return;
    lastOfflineErrorRef.current = offlineError;
    publishAppToast(offlineError, 'error');
  }, [offlineError]);

  return (
    <>
      <HeaderSection />

      <main className="min-h-screen pb-16">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          {loading ? (
            <PlaylistPageSkeleton />
          ) : error ? (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm text-red-700">{error}</p>
          ) : (
            <>
              <PlaylistPageHeader title={chapterTitle} breadcrumbs={breadcrumbs} />

              <ErrorBoundary
                resetKey={`${lessonId}-${playerResetKey}`}
                onError={handleVideoError}
                fallback={
                  <VideoPlayerFallback message={playerErrorMessage} onRetry={retryPlayer} />
                }
              >
                <PlaylistPlayerSection
                  key={`${lessonId}-${playerResetKey}`}
                  playlist={playlist}
                  activeLesson={activeLesson}
                  activeLessonId={lessonId}
                  onLessonSelect={selectLesson}
                  onLessonProgress={handleLessonProgress}
                  onLessonComplete={handleLessonComplete}
                  onVideoError={handleVideoError}
                />
              </ErrorBoundary>

              {activeLesson && <PlaylistLectureInfo lesson={activeLesson} />}

              <PlaylistChapterPicker
                chapters={chapters}
                activeChapterId={chapterId}
                onChapterChange={selectChapter}
              />

              <OfflineDownloadsPanel
                offlineVideos={offlineVideos}
                loading={offlineLoading}
                actionLoading={offlineActionLoading}
                onRemoveLesson={handleRemoveOfflineLesson}
                onRemoveAll={handleRemoveAllOffline}
              />

              <DoubtsDiscussionSection chapterId={chapterId} />
            </>
          )}
        </div>
      </main>

      <FooterSection />
    </>
  );
}
